/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService, FormStateBuilder, SmsService, smsViewDialogRoute } from '@gh/builder-shared';
import { DialogService, WaitingService } from '@gh/core-ui';
import { AppRouter, DateTimeTypes, filterableForm, FilterableFormState, getRouteInitData } from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import {
  buildDateTimeColumn,
  cursorTracker,
  equalsByKey,
  GridComponent,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { SmsListBusinessService } from '../../resources';
import { find, padStart } from 'lodash';
import { ROW_UNREAD_CLASS } from '@gh/common-internal-shared';
import { toString } from '@gh/core-util';

@Component({
  moduleId: module.id,
  selector: 'gh-sms-list',
  template: require('./sms-list.component.html'),
  styles: [require('./sms-list.component.scss')],
})
export class SmsListComponent implements OnInit {
  dataSource: SlicedDataSource<any>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  form: FilterableFormState<any>;
  isSelectAllDisabled: boolean = false;

  @ViewChild(GridComponent, {static: true}) grid: GridComponent;

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private businessService: SmsListBusinessService,
              private formStateBuilder: FormStateBuilder,
              private router: AppRouter,
              private waitings: WaitingService,
              private dialogService: DialogService,
              private smsService: SmsService,
              private navigationService: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  get selectedPartial(): boolean {
    return this.grid.isSelectedPartial();
  }

  get selectedAll(): boolean {
    return this.grid.isSelectedAll();
  }

  get isSelectedDate(): boolean {
    return this.form.value.dateFrom || this.form.value.dateTo;
  }

  ngOnInit(): void {
    const {gridLayout} = getRouteInitData(this.route);

    this.gridDefinition = this.buildGridDefinition();
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: {
        searchString: '',
        isContact: true,
        isUnknownContact: true,
        dateFrom: null,
        dateTo: null,
      },
      children: {
        searchString: void 0,
        isContact: void 0,
        isUnknownContact: void 0,
        dateFrom: void 0,
        dateTo: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form)).withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      const startDate = new Date(filter.dateFrom);
      const endDate = new Date(filter.dateTo);
      const fixedFilter = {
        searchString: filter.searchString || '',
        isContact: filter.isContact,
        isUnknownContact: filter.isUnknownContact,
        dateFrom: filter.dateFrom ? `${this.dateToStrung(startDate)}T00:00` : null,
        dateTo: filter.dateTo ? `${this.dateToStrung(endDate)}T23:59` : null,
      };
      this.dataSource = this.businessService.findAllAsDataSource(fixedFilter);
    });
  }

  onSelectAll(): void {
    const {selectedAll} = this;
    if (selectedAll) {
      this.deselectAllSmsItems();
    } else {
      this.selectAllSmsItems();
    }
  }

  onModelUpdated(): void {
    this.enableSelectAllControl();
    this.deselectAllSmsItems();
  }

  onBeforeModelUpdated(): void {
    // tslint:disable-next-line:no-magic-numbers
    setTimeout(() =>  this.enableSelectAllControl(false), 50);
    // tslint:disable-next-line:no-magic-numbers
    setTimeout(() => this.deselectAllSmsItems(), 100);
  }

  onSelect(sms: any): void {
    this.grid.selectRow(sms);
  }

  markAsReadOrUnread(value: boolean): void {
    const selectedIds = this.grid.selectedRows.map((el) => el.communicationId);

    this.businessService.markAsRead({
      entityIds: selectedIds,
      isMarkAsRead: value,
    }).pipe(this.waitings.operator()).subscribe(() => {
      this.form.emitFilter();
      this.smsService.sendName();
    });
  }

  isRowSelected(sms: any): boolean {
    const {selectionPrimaryKey} = this.grid;
    return !!find(this.grid.selectedRows, <PredicateFn<any>>equalsByKey(sms, selectionPrimaryKey));
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  buildGridDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased(
      {
        addedDate: {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          valueGetter: ({data}) => data.created,
        },
      },
      {
        getRowNodeId: (data: any) => data.communicationId,
        rowClassRules: {
          [ROW_UNREAD_CLASS]: ({data}: { data: any }) => !data.isMarkedAsRead,
        },
      });
  }

  onOpenMessage(sms: any): void {
    this.dialogService.openWithInput(smsViewDialogRoute, <any>{
      sms: {
        cellPhone: sms.data.cellPhone,
        message: sms.data.message,
        contactName: sms.data.contactName,
        contactId: sms.data.contactId,
        communicationId: sms.data.communicationId,
        isUnsubscribed: sms.data.isUnsubscribed,
        isMarkAsRead: sms.data.isMarkAsRead,
      },
    })
      .afterClosed().pipe()
      .subscribe((res) => {
        if (res && !!res.redirect) {
          this.router.navigateByUrl(`/contacts/${sms.data.contactId}`);
          return;
        }
        this.form.emitFilter();
      });
  }

  onReset(): void {
    this.form.setValue({
      searchString: this.form.value.searchString,
      dateFrom: null,
      dateTo: null,
      isContact: this.form.value.isContact,
      isUnknownContact: this.form.value.isUnknownContact,
    });
    this.form.emitFilter();
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0'); // tslint:disable-line:no-magic-numbers
  }
  private dateToStrung(date: Date): string {
    return `${[date.getFullYear(), date.getMonth() + 1, date.getDate()].map(this.stringifyDateComponent).join('-')}`;
  }

  private enableSelectAllControl(isEnabled: boolean = true): void {
    this.isSelectAllDisabled = !isEnabled;
  }

  /**
   * Removes selection of all sms items from grid
   */
  private deselectAllSmsItems(): void {
    this.grid.deselectAllRows();
  }

  /**
   * Add selection of current page sms items to grid
   */
  private selectAllSmsItems(): void {
    this.grid.selectAllRows();
  }
}
